import {
  MDSIcon,
  MDSIconKind,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSButton,
  MDSButtonVariant,
  MDSButtonSlotName,
  MDSProgressBar,
  MDSProgressBarColor,
} from "@/mds";
import { MutableRefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { MDSLoadingSpinner } from "@/mds/components/loading/loading-spinner";
import { MDSTooltip, MDSTooltipPlacement, MDSTooltipTrigger } from "@/mds/components/tooltip";
import { LoadingContainer } from "@/components/note/editor/source-bar/floating-preview-content.styles";
import { components } from "@/__openapi__/backend/schema";
import { AnimatePresence, motion } from "motion/react";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { resolveCapturedAudioSessionSyncModelUuid } from "@/modules/uuid/sync-models/resolveCapturedAudioSessionSyncModelUuid";
import { refineNoteWithTranscript } from "@/actions/refineNoteWithTranscript";
import { useAppStore } from "@/store";
import {
  AudioSourceUploadStateValue,
  getSourceUploadState,
} from "@/store/audio/pipeline/source-upload-state";
import { observer } from "mobx-react-lite";
import { toastModule } from "@/modules/toast";
import useDebouncedCallback from "@/domains/react/useDebouncedCallback";
import { MDSBubble, MDSBubbleAlignment, MDSBubbleVariant } from "@/mds/components/bubble";
import type { TranscriptSpeakerCurrentUser } from "@/modules/audio-recording/transcript-speaker-identity";
import type { TranscriptArrayItem } from "@/modules/audio-recording/transcript-types";
import styled from "@emotion/styled";
import { AudioSourceAvailabilityState } from "../audio-source-availability";
import { resolveAudioUnavailableStatusMessage } from "../audio-source-copy";
import {
  buildFloatingAudioPreviewTranscriptBubbles,
  type FloatingAudioPreviewTranscriptBubble as FloatingAudioPreviewTranscriptBubbleEntry,
} from "./floating-audio-preview-transcript";
import { TranscriptSpeakerBadge } from "@/components/note/transcript-speaker-badge";

type NoteSourceAudioArtifactData = components["schemas"]["AudioArtifactInternalResponseSchema"];

type TProps = {
  isOpen: boolean;
  isLoading: boolean;
  artifactData: NoteSourceAudioArtifactData | null;
  playingSourceIdRef: MutableRefObject<string | null>;
  sourceId: string;
  noteId: string;
  noteTitle: string;
  isRetranscribing: boolean;
  availabilityState?: AudioSourceAvailabilityState;
  audioRecordingId?: string | null;
  downloadUrl: string | null;
  isPreparingDownload: boolean;
  downloadPrepareError: unknown;
};

export const FloatingAudioPreviewContent = observer(function FloatingAudioPreviewContent({
  isLoading,
  artifactData,
  isOpen,
  playingSourceIdRef,
  sourceId,
  noteId,
  noteTitle,
  isRetranscribing,
  availabilityState = AudioSourceAvailabilityState.Ready,
  audioRecordingId = null,
  downloadUrl,
  isPreparingDownload,
  downloadPrepareError,
}: TProps) {
  const { store } = useAppStore();
  const [isTransforming, setIsTransforming] = useState(false);
  const uploadPipeline = getSourceUploadState({ store, audioRecordingId });
  const uploadProgressPercent = uploadPipeline.progressPercent;
  const clampedUploadProgress =
    typeof uploadProgressPercent === "number" && Number.isFinite(uploadProgressPercent)
      ? Math.min(100, Math.max(0, Math.round(uploadProgressPercent)))
      : null;
  const isUploadProgressIndeterminate = clampedUploadProgress === null;
  const isUploadRunning = uploadPipeline.isUploading;

  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const duration = artifactData?.audio_duration_seconds ?? 0;
  const currentTranscriptSpeakerUser = resolveCurrentTranscriptSpeakerUser(store, {
    allowUniqueDisplayNameFallback: true,
  });
  const rawTranscriptEntries: TranscriptArrayItem[] | null = artifactData?.transcript_array
    ? (artifactData.transcript_array as TranscriptArrayItem[])
    : artifactData?.transcript_text
      ? artifactData.transcript_text
          .split("\n")
          .map(line => line.trim())
          .filter(line => line.length > 0)
          .map((line, index) => ({
            sort_key: `transcript-text-${index}`,
            transcript: line,
            channel: 1,
          }))
      : null;
  const transcriptEntries = rawTranscriptEntries
    ? buildFloatingAudioPreviewTranscriptBubbles({
        transcriptEntries: rawTranscriptEntries,
        currentUser: currentTranscriptSpeakerUser,
      })
    : null;
  const speakerAwareTranscriptContent = rawTranscriptEntries
    ? buildFloatingAudioPreviewTranscriptBubbles({
        transcriptEntries: rawTranscriptEntries,
        currentUser: currentTranscriptSpeakerUser,
        prefixSelfSpeaker: true,
        prefixSpeakerDisplayNames: true,
      })
        .map(entry => entry.text.trim())
        .filter(Boolean)
        .join(" ")
        .trim()
    : "";
  const transcriptContent =
    speakerAwareTranscriptContent || artifactData?.transcript_text?.trim() || "";
  const animationFrameRef = useRef<number>();
  const lastUpdateTimeRef = useRef<number>(0);
  const hasTrackedScrollRef = useRef(false);

  const SKIP_SECONDS = 5;

  useEffect(() => {
    if (!isOpen) hasTrackedScrollRef.current = false;
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && artifactData) {
      const sessionId = resolveCapturedAudioSessionSyncModelUuid({
        capturedAudioId: artifactData.captured_audio_id,
      });
      trackEvent(TrackedEvent.VoiceRecordingView, {
        note_id: noteId,
        captured_audio_id: artifactData.captured_audio_id,
        session_id: sessionId,
        note_title: noteTitle,
      });
    }
  }, [isOpen, artifactData, noteId, sourceId, noteTitle]);

  const updateProgress = (timestamp: number) => {
    if (!audioRef.current || !isPlaying) return;

    if (!lastUpdateTimeRef.current) {
      lastUpdateTimeRef.current = timestamp;
    }

    const elapsed = timestamp - lastUpdateTimeRef.current;
    if (elapsed > 16) {
      // Update roughly every 16ms (60fps)
      setCurrentTime(audioRef.current.currentTime);
      lastUpdateTimeRef.current = timestamp;
    }

    animationFrameRef.current = requestAnimationFrame(updateProgress);
  };

  useEffect(() => {
    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(updateProgress);
    } else {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    }

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  useEffect(() => {
    if (isPlaying || isTransforming || isRetranscribing) playingSourceIdRef.current = sourceId;
    else playingSourceIdRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRetranscribing, isTransforming, isPlaying]);

  useLayoutEffect(() => {
    if (isOpen && audioRef.current && downloadUrl) {
      const currentAudioRef = audioRef.current;

      currentAudioRef.load();
      currentAudioRef.addEventListener("ended", handleEnded);

      return () => {
        currentAudioRef.removeEventListener("ended", handleEnded);
      };
    }
  }, [isOpen, downloadUrl]);

  const handlePlayPause = () => {
    if (!downloadUrl) return;
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();

        if (artifactData) {
          const sessionId = resolveCapturedAudioSessionSyncModelUuid({
            capturedAudioId: artifactData.captured_audio_id,
          });
          trackEvent(TrackedEvent.VoiceRecordingAudioPlay, {
            note_id: noteId,
            captured_audio_id: artifactData.captured_audio_id,
            session_id: sessionId,
            note_title: noteTitle,
          });
        }
      }
      setIsPlaying(!isPlaying);
    }
  };

  const progressBarRef = useRef<HTMLDivElement>(null);
  const scrubbingLastCurrentTimeRef = useRef(-1);

  const getNewTime = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!progressBarRef.current) return 0;

    const newTime = (event.nativeEvent.offsetX / progressBarRef.current.clientWidth) * duration;
    return Math.max(0, Math.min(newTime, duration));
  };

  const handleProgressBarMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !progressBarRef.current) return;

    const newTime = getNewTime(event);
    scrubbingLastCurrentTimeRef.current = newTime;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);

    return true;
  };

  const handleProgressBarMouseMove = useDebouncedCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!audioRef.current || !progressBarRef.current || event.buttons !== 1) return;

      const newTime = getNewTime(event);
      const diff = Math.abs(newTime - scrubbingLastCurrentTimeRef.current);
      if (diff < 0.05) return true;

      scrubbingLastCurrentTimeRef.current = newTime;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);

      return true;
    },
    10,
    { trailing: true, leading: false }
  );

  const handleProgressBarMouseUp = () => {
    try {
      handleProgressBarMouseMove.flush();

      return true;
    } finally {
      scrubbingLastCurrentTimeRef.current = -1;
    }
  };

  const handleScroll = () => {
    if (!hasTrackedScrollRef.current && artifactData) {
      const sessionId = resolveCapturedAudioSessionSyncModelUuid({
        capturedAudioId: artifactData.captured_audio_id,
      });
      trackEvent(TrackedEvent.VoiceRecordingTranscriptScroll, {
        note_id: noteId,
        captured_audio_id: artifactData.captured_audio_id,
        session_id: sessionId,
        note_title: noteTitle,
      });
      hasTrackedScrollRef.current = true;
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
  };

  const handleSkip = (direction: "back" | "forward") => {
    if (audioRef.current) {
      let newTime =
        audioRef.current.currentTime + (direction === "back" ? -SKIP_SECONDS : SKIP_SECONDS);
      newTime = Math.max(0, Math.min(newTime, duration));
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleIncorporateIntoNote = async () => {
    if (!artifactData?.captured_audio_id || !audioRecordingId) return;
    setIsTransforming(true);
    try {
      const result = await refineNoteWithTranscript({
        noteId,
        audioRecordingId,
        capturedAudioId: artifactData.captured_audio_id,
        transcriptContent,
        audioDurationSeconds: artifactData?.audio_duration_seconds ?? null,
        store,
      });
      if (!result.success) {
        toastModule.triggerToast({ content: result.error });
      }
    } finally {
      setIsTransforming(false);
    }
  };

  const formatTime = (time: number) => {
    if (!isFinite(time)) return "00:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const isBusy = isTransforming || isRetranscribing;
  const isUnavailable = availabilityState !== AudioSourceAvailabilityState.Ready;
  const hasDownloadError = Boolean(downloadPrepareError);
  const isPreparingRecording =
    !isUnavailable && !hasDownloadError && (isPreparingDownload || !downloadUrl);
  const showLoadingOverlay = !hasDownloadError && (isLoading || isPreparingRecording);
  const showUploadBanner =
    availabilityState === AudioSourceAvailabilityState.Uploading ||
    (availabilityState === AudioSourceAvailabilityState.Ready && showLoadingOverlay);
  const showUploadErrorBanner = uploadPipeline.state === AudioSourceUploadStateValue.Error;
  const showUnavailableMessage = isUnavailable && !showUploadBanner && !showUploadErrorBanner;
  const shouldShowTranscriptSection = availabilityState !== AudioSourceAvailabilityState.Recording;
  const shouldShowRefineButton =
    availabilityState === AudioSourceAvailabilityState.Ready &&
    Boolean(artifactData?.captured_audio_id) &&
    !isLoading;
  const isRefineUnsupported = !audioRecordingId;
  const refineTooltipConfig = isRefineUnsupported
    ? { label: "Not supported for this audio recording", placement: MDSTooltipPlacement.Top }
    : undefined;
  const canShowPlayer =
    availabilityState === AudioSourceAvailabilityState.Ready &&
    Boolean(downloadUrl) &&
    !isPreparingDownload &&
    !hasDownloadError &&
    !isLoading;
  const isPlaybackDisabled = isPreparingRecording;
  const preparingTooltipConfig = isPreparingRecording
    ? { label: "Preparing recording...", placement: MDSTooltipPlacement.Top }
    : undefined;
  const statusMessage = resolveAudioUnavailableStatusMessage({
    availabilityState,
  });

  return (
    <Card>
      {showUnavailableMessage ? (
        <LoadingContainer>
          <StatusMessage>
            {availabilityState !== AudioSourceAvailabilityState.Error &&
              availabilityState !== AudioSourceAvailabilityState.Skipped && (
                <MDSLoadingSpinner size={24} />
              )}
            <MDSText variant={MDSTextVariant.TextSm} weight={MDSTextWeight.SemiBold}>
              {statusMessage}
            </MDSText>
          </StatusMessage>
        </LoadingContainer>
      ) : hasDownloadError ? (
        <LoadingContainer>
          <StatusMessage>
            <MDSText variant={MDSTextVariant.TextSm} weight={MDSTextWeight.SemiBold}>
              Failed to prepare recording
            </MDSText>
          </StatusMessage>
        </LoadingContainer>
      ) : (
        <>
          {showUploadBanner && (
            <StatusBanner $tone="upload">
              <StatusBannerRow>
                <UploadProgressBar
                  minValue={0}
                  maxValue={100}
                  value={clampedUploadProgress ?? undefined}
                  isIndeterminate={isUploadProgressIndeterminate}
                  color={MDSProgressBarColor.Emphasis}
                  size="large"
                />
                <StatusBannerText
                  $tone="upload"
                  variant={MDSTextVariant.TextXs}
                  weight={MDSTextWeight.SemiBold}
                >
                  {clampedUploadProgress !== null
                    ? `${clampedUploadProgress}% Uploaded`
                    : "Uploading..."}
                </StatusBannerText>
              </StatusBannerRow>
            </StatusBanner>
          )}
          {showUploadErrorBanner && (
            <StatusBanner $tone="error">
              <StatusBannerRow>
                <ErrorMessageRow>
                  <MDSIcon
                    kind={MDSIconKind.CircleExclamation}
                    size={16}
                    overrideColor="currentColor"
                  />
                  <StatusBannerText $tone="error" variant={MDSTextVariant.TextSm}>
                    Upload failed. Please check your connection and try again.
                  </StatusBannerText>
                </ErrorMessageRow>
                <MDSButton
                  variant={MDSButtonVariant.Danger}
                  isDisabled={!audioRecordingId || isUploadRunning}
                  onClick={() => {
                    if (!audioRecordingId) return;
                    void store.audio.startSourceUpload({ audioRecordingId, forceRestart: true });
                  }}
                >
                  Retry source upload
                </MDSButton>
              </StatusBannerRow>
            </StatusBanner>
          )}
          <AnimatePresence initial={false}>
            {canShowPlayer && (
              <MDSTooltip config={preparingTooltipConfig}>
                <MDSTooltipTrigger>
                  <AudioPlayerContainer
                    key="audio-player"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.12 }}
                  >
                    <AudioControlsContainer $disabled={isPlaybackDisabled}>
                      <StyledProgressBar
                        ref={progressBarRef}
                        onMouseDown={handleProgressBarMouseDown}
                        onMouseUp={handleProgressBarMouseUp}
                        onMouseMove={handleProgressBarMouseMove}
                        $disabled={isPlaybackDisabled}
                      >
                        <ProgressFill
                          $disabled={isPlaybackDisabled}
                          style={{ width: `${(currentTime / (duration || 1)) * 100}%` }}
                        />
                      </StyledProgressBar>
                      <ControlsRow>
                        <TimeText variant={MDSTextVariant.TextSm} weight={MDSTextWeight.SemiBold}>
                          {formatTime(currentTime)}
                        </TimeText>
                        <ControlButtons>
                          <ControlButton
                            onClick={() => handleSkip("back")}
                            disabled={isPlaybackDisabled}
                          >
                            <MDSIcon kind={MDSIconKind.SkipBackward} size={16} />
                          </ControlButton>
                          <ControlButton onClick={handlePlayPause} disabled={isPlaybackDisabled}>
                            <MDSIcon
                              kind={isPlaying ? MDSIconKind.PauseSolid : MDSIconKind.PlaySolid}
                              size={20}
                            />
                          </ControlButton>
                          <ControlButton
                            onClick={() => handleSkip("forward")}
                            disabled={isPlaybackDisabled}
                          >
                            <MDSIcon kind={MDSIconKind.SkipForward} size={16} />
                          </ControlButton>
                        </ControlButtons>
                        <TimeTextRight
                          variant={MDSTextVariant.TextSm}
                          weight={MDSTextWeight.SemiBold}
                        >
                          {formatTime(duration)}
                        </TimeTextRight>
                      </ControlsRow>
                    </AudioControlsContainer>
                    <audio
                      ref={audioRef}
                      src={downloadUrl ?? undefined}
                      onEnded={handleEnded}
                      style={{ display: "none" }}
                    />
                  </AudioPlayerContainer>
                </MDSTooltipTrigger>
              </MDSTooltip>
            )}
          </AnimatePresence>
        </>
      )}
      {shouldShowTranscriptSection && (
        <TranscriptScrollArea onScroll={handleScroll}>
          {transcriptEntries && (
            <TranscriptList>
              {groupFloatingAudioPreviewTranscriptEntries(transcriptEntries).map(group => (
                <FloatingAudioPreviewTranscriptBubbleGroup key={group.key} group={group} />
              ))}
            </TranscriptList>
          )}
        </TranscriptScrollArea>
      )}
      {shouldShowRefineButton && (
        <ButtonContainer>
          {refineTooltipConfig ? (
            <MDSTooltip config={refineTooltipConfig}>
              <MDSTooltipTrigger>
                <MDSButton
                  variant={MDSButtonVariant.Contrast}
                  isLoading={isBusy}
                  onClick={handleIncorporateIntoNote}
                  isDisabled={isBusy || isRefineUnsupported}
                >
                  <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Sparkle} />
                  Refine note with transcript
                </MDSButton>
              </MDSTooltipTrigger>
            </MDSTooltip>
          ) : (
            <MDSButton
              variant={MDSButtonVariant.Contrast}
              isLoading={isBusy}
              onClick={handleIncorporateIntoNote}
              isDisabled={isBusy}
            >
              <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Sparkle} />
              Refine note with transcript
            </MDSButton>
          )}
        </ButtonContainer>
      )}
      <AnimatePresence initial={false}>
        {showLoadingOverlay && (
          <LoadingOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
          >
            <MDSLoadingSpinner size={24} />
          </LoadingOverlay>
        )}
      </AnimatePresence>
    </Card>
  );
});

export interface FloatingAudioPreviewTranscriptBubbleGroupEntry {
  key: string;
  alignment: MDSBubbleAlignment;
  entries: FloatingAudioPreviewTranscriptBubbleEntry[];
}

function FloatingAudioPreviewTranscriptBubbleGroup({
  group,
}: {
  group: FloatingAudioPreviewTranscriptBubbleGroupEntry;
}) {
  const firstEntry = group.entries[0];
  const speakerDisplayName =
    firstEntry?.variant === MDSBubbleVariant.VoiceOthers
      ? firstEntry.speakerDisplayName
      : undefined;

  return (
    <PreviewTranscriptBubbleGroup $align={group.alignment}>
      {speakerDisplayName && (
        <PreviewSpeakerBadgeRow>
          <TranscriptSpeakerBadge name={speakerDisplayName} />
        </PreviewSpeakerBadgeRow>
      )}
      {group.entries.map((entry, index) => (
        <StyledTranscriptBubble
          key={entry.key}
          align={entry.alignment}
          variant={entry.variant}
          isLastInGroup={index === group.entries.length - 1}
        >
          <TranscriptBubbleContent>
            <span>{entry.text}</span>
          </TranscriptBubbleContent>
        </StyledTranscriptBubble>
      ))}
    </PreviewTranscriptBubbleGroup>
  );
}

export function groupFloatingAudioPreviewTranscriptEntries(
  entries: FloatingAudioPreviewTranscriptBubbleEntry[]
): FloatingAudioPreviewTranscriptBubbleGroupEntry[] {
  const groups: FloatingAudioPreviewTranscriptBubbleGroupEntry[] = [];

  for (const entry of entries) {
    const previousGroup = groups[groups.length - 1];
    const previousEntry = previousGroup?.entries[previousGroup.entries.length - 1];

    if (
      previousGroup &&
      previousEntry &&
      !previousEntry.isLastInGroup &&
      isSamePreviewTranscriptSpeaker(entry, previousEntry)
    ) {
      previousGroup.entries.push(entry);
    } else {
      groups.push({ key: entry.key, alignment: entry.alignment, entries: [entry] });
    }
  }

  return groups;
}

function isSamePreviewTranscriptSpeaker(
  entry: FloatingAudioPreviewTranscriptBubbleEntry,
  previousEntry: FloatingAudioPreviewTranscriptBubbleEntry
): boolean {
  if (hasSpeakerGroupKeys(entry, previousEntry)) {
    return previousEntry.speakerGroupKey === entry.speakerGroupKey;
  }

  return (
    previousEntry.alignment === entry.alignment &&
    previousEntry.variant === entry.variant &&
    previousEntry.speakerDisplayName === entry.speakerDisplayName
  );
}

/** Returns whether both adjacent bubbles can use stable speaker identity for grouping. */
function hasSpeakerGroupKeys(
  entry: FloatingAudioPreviewTranscriptBubbleEntry,
  previousEntry: FloatingAudioPreviewTranscriptBubbleEntry
): boolean {
  return previousEntry.speakerGroupKey != null && entry.speakerGroupKey != null;
}

/**
 * Provides the attachment preview with signed-in user identity so saved meeting
 * transcript turns can recover the normal right-side self bubble when Recall
 * supplies a matching participant email or unique full display name.
 */
function resolveCurrentTranscriptSpeakerUser(
  store: {
    account: {
      myAccount: {
        profileEmailAddress?: string | null;
        profileDisplayName?: string | null;
      };
    };
  },
  {
    allowUniqueDisplayNameFallback = false,
  }: {
    allowUniqueDisplayNameFallback?: boolean;
  } = {}
): TranscriptSpeakerCurrentUser {
  const myAccount = store.account.myAccount;

  return {
    email: myAccount.profileEmailAddress,
    displayName: myAccount.profileDisplayName,
    allowUniqueDisplayNameFallback,
  };
}

const Card = styled.div`
  padding: 0;
  width: min(600px, 95vw);
  max-width: 95vw;
  min-height: 440px;
  max-height: 440px;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const StatusMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const UploadProgressBar = styled(MDSProgressBar)`
  flex: 1;
  min-width: 240px;
`;

const AudioPlayerContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: ${({ theme }) => theme.spacing["4"]};
`;

const AudioControlsContainer = styled.div<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
`;

const StyledProgressBar = styled.div<{ $disabled?: boolean }>`
  width: 100%;
  height: 14px;
  background: ${({ theme }) => theme.colors.icon.placeholder};
  border-radius: 7px;
  overflow: hidden;
  position: relative;
  user-select: none;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
`;

const ProgressFill = styled(motion.div)<{ $disabled?: boolean }>`
  height: 100%;
  background: ${({ theme, $disabled }) =>
    $disabled ? theme.colors.icon.secondary : theme.colors.icon.primary};
  border-radius: 7px;
  will-change: width;
  user-select: none;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
`;

const TimeText = styled(MDSText)`
  width: 60px;
`;

const TimeTextRight = styled(TimeText)`
  text-align: right;
`;

const ControlsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const ControlButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.icon.secondary};
  transition: background 0.2s;
  &:hover {
    background: ${({ theme }) => theme.colors.background.button.default.hover};
  }
  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.icon.placeholder};
  }
  &:disabled:hover {
    background: transparent;
  }
`;

const TranscriptScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const TranscriptList = styled.div`
  padding: ${({ theme }) => `${theme.spacing["2"]} ${theme.spacing["4"]}`};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["4"]};
`;

const PreviewTranscriptBubbleGroup = styled.div<{ $align: MDSBubbleAlignment }>`
  align-items: ${({ $align }) =>
    $align === MDSBubbleAlignment.Left
      ? "flex-start"
      : $align === MDSBubbleAlignment.Center
        ? "center"
        : "flex-end"};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["0.5"]};
  width: 100%;
`;

const PreviewSpeakerBadgeRow = styled.div`
  max-width: 100%;
  width: fit-content;
`;

const StyledTranscriptBubble = styled(MDSBubble)`
  width: 100%;

  & > * {
    max-width: ${({ theme }) => `calc(100% - ${theme.spacing[5]})`};

    ${({ align, theme }) =>
      align === MDSBubbleAlignment.Left
        ? `margin-right: ${theme.spacing[5]};`
        : `margin-left: ${theme.spacing[5]};`};
  }
`;

const TranscriptBubbleContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["1"]};
  max-width: 100%;
  min-width: 0;
`;

const StatusBanner = styled.div<{ $tone?: "upload" | "error" }>`
  background: ${({ theme, $tone }) =>
    $tone === "error" ? theme.colors.background.banner.danger : theme.colors.surface.previewHeader};
  border-bottom: none;
  padding: ${({ theme, $tone }) =>
    $tone === "upload"
      ? `0 ${theme.spacing["5"]} ${theme.spacing["2"]}`
      : `${theme.spacing["3"]} ${theme.spacing["4"]}`};
`;

const StatusBannerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing["3"]};
`;

const ErrorMessageRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
  color: ${({ theme }) => theme.colors.text.danger};
  flex: 1;
`;

const StatusBannerText = styled(MDSText)<{ $tone?: "upload" | "error" }>`
  white-space: nowrap;
  color: ${({ theme, $tone }) => {
    if ($tone === "upload") return theme.colors.icon.emphasis;
    if ($tone === "error") return theme.colors.text.danger;
    return theme.colors.text.secondary;
  }};
`;

const LoadingOverlay = styled(motion.div)`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.surface.preview};
  pointer-events: all;
`;

const ControlButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${({ theme }) => `${theme.spacing["4"]}`};
`;
